/**
 * Les écrans des présences : les tuiles par groupe, la feuille d'un groupe
 * (Présences | À savoir), la fiche de l'enfant, les photos.
 *
 * Nés pour le téléphone de l'animateur (`/presences/:jeton`, 24-25/10/2026),
 * ils servent aussi à la coordination dans Gestion › Présences (25/10/2026) :
 * mêmes gestes, tous les enfants du centre. Ce qui change, c'est la
 * `source` — un lien et son code, ou la session de la coordination — et la
 * navigation : l'adresse du navigateur pour le lien, un état pour l'espace
 * de gestion. Les écrans ne savent ni d'où viennent les données ni où ils
 * vivent ; `ContexteDesPresences` le leur dit.
 *
 * Chaque geste s'enregistre aussitôt dans `presences`, la table de l'écran
 * de gestion, du rapport ONE et de la facturation.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  Camera,
  CameraOff,
  Check,
  ChevronLeft,
  ChevronRight,
  HeartPulse,
  Loader2,
  MessageSquare,
  Phone,
  RefreshCw,
  Search,
  Users,
  Waves,
  X,
} from 'lucide-react';
import {
  ageEnClair,
  alertesDeLaFiche,
  appliquerNote,
  appliquerPointage,
  cleDeGroupe,
  compterLeJour,
  dateEnClair,
  etatDuJour,
  filtrerEnfants,
  grouperParStage,
  jourParDefaut,
  jourPointable,
  messageDeRefus,
  NOM_COURT_DU_JOUR,
  NOM_DU_JOUR,
  prochainEtat,
  remplacerPresence,
  semaineEnClair,
  trouverGroupe,
  type EnfantDuLien,
  type EtatDuJour,
  type FicheEnfant,
  type GroupeDAppel,
  type JourCle,
  type ListeDAppel,
} from '../../lib/presences/lien';
import {
  indicateurs,
  initiales,
  partagerLeGroupe,
  type EssentielsDeLEnfant,
  type LigneASavoir,
} from '../../lib/presences/essentiels';
import type { SourceDesPresences, Vue } from '../../lib/presences/source';
import { couleurDeReglement } from '../../lib/presences/session';
import type { ReglementAffiche } from '../../lib/inscriptions/reglement';

/* Les adresses des photos valent une heure ; on les redemande un peu avant. */
const DUREE_DES_PHOTOS_MS = 50 * 60 * 1000;

const aujourdHui = (): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/* ------------------------------------------------------------------------ */
/*  Où l'on est, et comment on y va                                          */
/* ------------------------------------------------------------------------ */

export interface ContexteDesPresences {
  /** L'adresse d'une vue quand la navigation passe par le navigateur ; `null` sinon (un état). */
  lienVers: (vue: Vue) => string | null;
  naviguer: (vue: Vue) => void;
  /** La classe `top-*` des en-têtes collants : `top-0` seul, plus bas sous la barre de Gestion. */
  hautCollant: string;
  /** Pleine page (le lien) ou dans la mise en page de Gestion. */
  pleinePage: boolean;
}

const Contexte = createContext<ContexteDesPresences>({
  lienVers: () => null,
  naviguer: () => undefined,
  hautCollant: 'top-0',
  pleinePage: true,
});

/** Un lien vers une vue : `<Link>` quand il y a une adresse, un bouton sinon. */
function Vers({
  vue,
  className,
  children,
  'aria-label': ariaLabel,
}: {
  vue: Vue;
  className?: string;
  children: React.ReactNode;
  'aria-label'?: string;
}) {
  const { lienVers, naviguer } = useContext(Contexte);
  const href = lienVers(vue);
  if (href) {
    return (
      <Link to={href} className={className} aria-label={ariaLabel}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" onClick={() => naviguer(vue)} className={`${className ?? ''} text-left`} aria-label={ariaLabel}>
      {children}
    </button>
  );
}

const VUE_GROUPES: Vue = { nom: 'groupes' };

export interface EcransDesPresencesProps {
  source: SourceDesPresences;
  vue: Vue;
  lienVers: (vue: Vue) => string | null;
  naviguer: (vue: Vue) => void;
  /** Le lien : la source demande le code (manquant ou faux). La page montre l'écran du code. */
  onCodeRequis?: () => void;
  /** La coordination : le règlement de chaque inscription, qui colore la ligne. */
  lireReglements?: (registrationIds: string[]) => Promise<Map<string, ReglementAffiche>>;
  /** La coordination : une tuile « Tout le centre », tous les enfants de la semaine avec leur groupe. */
  toutLeCentre?: boolean;
  hautCollant?: string;
  pleinePage?: boolean;
}

export function EcransDesPresences({
  source,
  vue,
  lienVers,
  naviguer,
  onCodeRequis,
  lireReglements,
  toutLeCentre = false,
  hautCollant = 'top-0',
  pleinePage = true,
}: EcransDesPresencesProps) {
  const contexte = useMemo<ContexteDesPresences>(
    () => ({ lienVers, naviguer, hautCollant, pleinePage }),
    [lienVers, naviguer, hautCollant, pleinePage],
  );

  const [liste, setListe] = useState<ListeDAppel | null>(null);
  const [chargement, setChargement] = useState(true);
  const [rafraichissement, setRafraichissement] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [jour, setJour] = useState<JourCle | null>(null);
  const [enCours, setEnCours] = useState<Set<string>>(new Set());
  const jourChoisiParLAnim = useRef(false);
  /*
    Les essentiels (santé, départ, image) et les photos : lus une fois quand la
    liste est là, pas toutes les trente secondes — ils ne bougent pas dans la
    journée. Le bouton ↻ les relit aussi.
  */
  const [essentiels, setEssentiels] = useState<Map<string, EssentielsDeLEnfant> | null>(null);
  const [essentielsErreur, setEssentielsErreur] = useState<string | null>(null);
  const [photos, setPhotos] = useState<Map<string, string>>(new Map());
  const photosLuesLe = useRef(0);
  const essentielsEnCours = useRef(false);
  /* La coordination seulement : le règlement de chaque inscription. */
  const [reglements, setReglements] = useState<Map<string, ReglementAffiche> | null>(null);

  useEffect(() => {
    if (pleinePage) document.title = liste ? `Présences · ${liste.centre.nom}` : 'Présences';
  }, [liste, pleinePage]);

  const charger = useCallback(
    async (silencieux: boolean) => {
      if (silencieux) setRafraichissement(true);
      else setChargement(true);
      setErreur(null);
      try {
        const lecture = await source.lireListe();
        if (lecture.codeRequis) {
          setListe(null);
          onCodeRequis?.();
          return;
        }
        setRefus(null);
        setListe(lecture.liste);
        if (!jourChoisiParLAnim.current) {
          setJour(jourParDefaut(lecture.liste.jours, aujourdHui()));
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        if (silencieux && liste) setErreur(messageDeRefus(message));
        else setRefus(messageDeRefus(message));
      } finally {
        setChargement(false);
        setRafraichissement(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, liste],
  );

  useEffect(() => {
    void charger(false);
    // La source change (un autre code, un autre centre, une autre semaine) : on relit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  /*
    Plusieurs animateurs sur le même centre : chacun ne voit les gestes des
    autres qu'en relisant. On relit en revenant sur l'onglet, et toutes les
    trente secondes tant que l'écran est visible — jamais pendant qu'un tap
    attend sa réponse, pour ne pas lui passer dessus. Une lecture, c'est une
    requête : trente téléphones font une requête par seconde en tout.
  */
  const enCoursRef = useRef(enCours);
  enCoursRef.current = enCours;
  useEffect(() => {
    if (!liste) return;
    const relire = () => {
      if (document.visibilityState !== 'visible' || enCoursRef.current.size > 0) return;
      void charger(true);
    };
    document.addEventListener('visibilitychange', relire);
    const minuterie = window.setInterval(relire, 30_000);
    return () => {
      document.removeEventListener('visibilitychange', relire);
      window.clearInterval(minuterie);
    };
  }, [charger, liste]);

  const codeRedemande = () => {
    setEssentiels(null);
    setPhotos(new Map());
    photosLuesLe.current = 0;
    onCodeRequis?.();
  };

  const chargerEssentiels = async () => {
    if (essentielsEnCours.current) return;
    essentielsEnCours.current = true;
    try {
      setEssentielsErreur(null);
      const lecture = await source.lireEssentiels();
      if (lecture.codeRequis) return codeRedemande();
      setEssentiels(lecture.parInscription);
      if (Date.now() - photosLuesLe.current > DUREE_DES_PHOTOS_MS) {
        // Les photos à part : sans elles, les présences et « À savoir » marchent, avec les initiales.
        try {
          const lues = await source.lirePhotos();
          if (lues.codeRequis) return codeRedemande();
          photosLuesLe.current = Date.now();
          setPhotos(lues.photos);
        } catch (e) {
          console.error('[presences] photos illisibles, initiales en attendant', e);
        }
      }
    } catch (e) {
      setEssentielsErreur(messageDeRefus(e instanceof Error ? e.message : String(e)));
    } finally {
      essentielsEnCours.current = false;
    }
  };

  useEffect(() => {
    if (liste && essentiels === null) void chargerEssentiels();
    // La liste est là, les essentiels pas encore : on les lit. Une fois.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liste, essentiels]);

  /* Le règlement (coordination) : lu avec la liste, relu au ↻ ; un échec laisse les lignes sans couleur. */
  const chargerReglements = async (enfants: readonly EnfantDuLien[]) => {
    if (!lireReglements) return;
    try {
      setReglements(await lireReglements(enfants.map((e) => e.registrationId)));
    } catch (e) {
      console.error('[presences] règlement illisible', e);
      setReglements(null);
    }
  };
  useEffect(() => {
    if (liste && lireReglements && reglements === null) void chargerReglements(liste.enfants);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liste, lireReglements]);

  const marquerEnCours = (ids: readonly string[], actif: boolean) =>
    setEnCours((prev) => {
      const s = new Set(prev);
      ids.forEach((id) => (actif ? s.add(id) : s.delete(id)));
      return s;
    });

  const signaler = (e: unknown) => setErreur(messageDeRefus(e instanceof Error ? e.message : String(e)));

  /*
    Chaque geste ne touche que SON enfant, et toujours à partir de la liste du
    moment (`setListe(l => …)`) : une relecture ou le geste d'un collègue
    arrivé entre-temps n'est pas écrasé. Si la base refuse, seul cet enfant
    revient comme avant.
  */
  const changerEnfants = (f: (enfants: EnfantDuLien[]) => EnfantDuLien[]) =>
    setListe((l) => (l ? { ...l, enfants: f(l.enfants) } : l));

  /* Un tap sur la case : l'état tourne, la base est écrite, la ligne reflète ce qu'elle a gardé. */
  const taper = async (enfant: EnfantDuLien) => {
    if (!liste || !jour || enCours.has(enfant.registrationId)) return;
    const present = prochainEtat(etatDuJour(enfant, jour));
    const avant = enfant.presence;
    changerEnfants((e) => appliquerPointage(e, enfant.registrationId, jour, present));
    marquerEnCours([enfant.registrationId], true);
    setErreur(null);
    try {
      const r = await source.pointer(enfant.registrationId, jour, present);
      if (r.codeRequis) return codeRedemande();
      changerEnfants((e) => remplacerPresence(e, enfant.registrationId, r.presence));
    } catch (e) {
      changerEnfants((es) => remplacerPresence(es, enfant.registrationId, avant));
      signaler(e);
    } finally {
      marquerEnCours([enfant.registrationId], false);
    }
  };

  const noterEnfant = async (registrationId: string, notes: string) => {
    if (!liste) return;
    const avant = liste.enfants.find((e) => e.registrationId === registrationId)?.presence ?? null;
    changerEnfants((e) => appliquerNote(e, registrationId, notes));
    try {
      const r = await source.noter(registrationId, notes);
      if (r.codeRequis) return codeRedemande();
      changerEnfants((e) => remplacerPresence(e, registrationId, r.presence));
    } catch (e) {
      changerEnfants((es) => remplacerPresence(es, registrationId, avant));
      signaler(e);
    }
  };

  const tousLesAutresPresents = async (enfants: EnfantDuLien[]) => {
    if (!liste || !jour) return;
    const aPointer = enfants.filter((e) => etatDuJour(e, jour) === 'a_pointer');
    if (aPointer.length === 0) return;
    const ids = aPointer.map((e) => e.registrationId);
    const avant = new Map(aPointer.map((e) => [e.registrationId, e.presence]));
    changerEnfants((es) => ids.reduce((acc, id) => appliquerPointage(acc, id, jour, true), es));
    marquerEnCours(ids, true);
    setErreur(null);
    try {
      const r = await source.pointerLesAutres(ids, jour, true);
      if (r.codeRequis) return codeRedemande();
      changerEnfants((es) =>
        r.presences.reduce((acc, { registrationId, ...presence }) => remplacerPresence(acc, registrationId, presence), es),
      );
    } catch (e) {
      changerEnfants((es) => ids.reduce((acc, id) => remplacerPresence(acc, id, avant.get(id) ?? null), es));
      signaler(e);
    } finally {
      marquerEnCours(ids, false);
    }
  };

  const groupes = useMemo(() => (liste ? grouperParStage(liste.enfants) : []), [liste]);
  const jourCourant = liste?.jours.find((j) => j.cle === jour) ?? null;

  /* Tout le centre : un seul groupe, les enfants par prénom, leur groupe sous le nom. */
  const centreEntier = useMemo<GroupeDAppel | null>(
    () =>
      liste
        ? {
            titre: 'Tout le centre',
            stage: 'Tout le centre',
            sousGroupe: null,
            enfants: [...liste.enfants].sort(
              (a, b) => a.prenom.localeCompare(b.prenom, 'fr') || a.nom.localeCompare(b.nom, 'fr'),
            ),
          }
        : null,
    [liste],
  );

  /* La fiche revient d'où l'on venait : un groupe ou tout le centre. */
  const provenance = useRef<Vue>(VUE_GROUPES);
  if (vue.nom === 'groupe' || vue.nom === 'centre') provenance.current = vue;

  /* ---------------------------------------------------------------------- */

  if (chargement) {
    return (
      <Contexte.Provider value={contexte}>
      <Cadre>
        <div className="flex flex-col items-center justify-center py-24 text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="mt-3 text-sm">Chargement de la liste…</p>
        </div>
      </Cadre>
      </Contexte.Provider>
    );
  }

  if (refus) {
    return (
      <Contexte.Provider value={contexte}>
      <Cadre>
        <div className="mx-4 mt-16 rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-red-500" />
          <h1 className="mt-3 text-lg font-bold text-red-800">La liste ne s’ouvre pas</h1>
          <p className="mt-2 text-red-700">{refus}</p>
          <button
            type="button"
            onClick={() => void charger(false)}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-medium text-gray-800 shadow-sm ring-1 ring-gray-200"
          >
            <RefreshCw className="h-4 w-4" /> Réessayer
          </button>
        </div>
      </Cadre>
      </Contexte.Provider>
    );
  }

  /* Pas de liste et pas de refus : la source a demandé le code, la page a repris la main. */
  if (!liste) return null;

  const enTete = (
    <EnTete
      liste={liste}
      rafraichissement={rafraichissement}
      onRecharger={() => {
        void charger(true);
        void chargerEssentiels();
        void chargerReglements(liste.enfants);
      }}
    />
  );

  let ecran: React.ReactNode;

  if (vue.nom === 'enfant') {
    const enfant = liste.enfants.find((e) => e.registrationId === vue.registrationId) ?? null;
    const groupe = enfant ? groupes.find((g) => g.enfants.some((e) => e.registrationId === enfant.registrationId)) : null;
    ecran = (
      <Cadre>
        <FicheDeLEnfant
          source={source}
          enfant={enfant}
          photo={photos.get(vue.registrationId) ?? null}
          retour={provenance.current.nom === 'centre' ? provenance.current : groupe ? { nom: 'groupe', cle: cleDeGroupe(groupe) } : VUE_GROUPES}
          onNoter={(n) => void noterEnfant(vue.registrationId, n)}
          onCodeRequis={codeRedemande}
        />
      </Cadre>
    );
  } else if (vue.nom === 'centre' && centreEntier) {
    ecran = (
      <Cadre>
        {enTete}
        <FeuilleDuGroupe
          key="tout-le-centre"
          groupe={centreEntier}
          montrerGroupe
          jours={liste.jours}
          jour={jour}
          jourCourant={jourCourant}
          erreur={erreur}
          enCours={enCours}
          essentiels={essentiels}
          essentielsErreur={essentielsErreur}
          photos={photos}
          reglements={reglements}
          onRelireEssentiels={() => void chargerEssentiels()}
          onJour={(j) => {
            jourChoisiParLAnim.current = true;
            setJour(j);
          }}
          onTaper={(e) => void taper(e)}
          onTousLa={(enfants) => void tousLesAutresPresents(enfants)}
        />
      </Cadre>
    );
  } else if (vue.nom === 'groupe') {
    const groupe = trouverGroupe(groupes, vue.cle);
    if (!groupe) {
      ecran = (
        <Cadre>
          {enTete}
          <main className="px-4 py-10 text-center text-gray-600">
            <p>Ce groupe n’existe plus dans la liste.</p>
            <Vers vue={VUE_GROUPES} className="mt-4 inline-block font-medium text-primary-700">
              Revenir aux groupes
            </Vers>
          </main>
        </Cadre>
      );
    } else {
    ecran = (
      <Cadre>
        {enTete}
        <FeuilleDuGroupe
          key={groupe.titre}
          groupe={groupe}
          jours={liste.jours}
          jour={jour}
          jourCourant={jourCourant}
          erreur={erreur}
          enCours={enCours}
          essentiels={essentiels}
          essentielsErreur={essentielsErreur}
          photos={photos}
          reglements={reglements}
          onRelireEssentiels={() => void chargerEssentiels()}
          onJour={(j) => {
            jourChoisiParLAnim.current = true;
            setJour(j);
          }}
          onTaper={(e) => void taper(e)}
          onTousLa={(enfants) => void tousLesAutresPresents(enfants)}
        />
      </Cadre>
    );
    }
  } else {
  ecran = (
    <Cadre>
      {enTete}
      <main className="px-4 pb-16 pt-4">
        {erreur && <Erreur texte={erreur} />}
        {jourCourant && (
          <p className="mb-3 text-sm text-gray-600">
            {NOM_DU_JOUR[jourCourant.cle]} {dateEnClair(jourCourant.date).slice(0, 5)} · choisissez votre groupe
          </p>
        )}
        {groupes.length === 0 ? (
          <div className="py-16 text-center text-gray-500">
            <Users className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-2">Aucun enfant inscrit dans ce centre cette semaine.</p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {toutLeCentre && centreEntier && (
              <li className="sm:col-span-2">
                <Vers
                  vue={{ nom: 'centre' }}
                  className="flex w-full items-center gap-3 rounded-2xl bg-primary-600 p-4 text-left text-white shadow-sm active:bg-primary-700"
                >
                  <Users className="h-7 w-7 flex-shrink-0 text-primary-100" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-lg font-semibold">Tout le centre</p>
                    <p className="mt-0.5 text-sm text-primary-100">
                      {centreEntier.enfants.length} enfant{centreEntier.enfants.length > 1 ? 's' : ''}, par prénom, avec leur groupe
                      {jour && (() => {
                        const c = compterLeJour(centreEntier.enfants, jour);
                        return c.aPointer < centreEntier.enfants.length ? ` · ${c.presents} ✓ ${c.absents} ✗ · ${c.aPointer} à pointer` : '';
                      })()}
                    </p>
                  </div>
                  <ChevronRight className="h-6 w-6 flex-shrink-0 text-primary-200" />
                </Vers>
              </li>
            )}
            {groupes.map((g) => {
              const c = jour ? compterLeJour(g.enfants, jour) : null;
              return (
                <li key={g.titre}>
                  <Vers
                    vue={{ nom: 'groupe', cle: cleDeGroupe(g) }}
                    className="flex w-full items-center gap-3 rounded-2xl bg-white p-4 text-left shadow-sm ring-1 ring-gray-200 active:bg-gray-50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-lg font-semibold text-gray-900">{g.stage}</p>
                      {g.sousGroupe && <p className="truncate text-sm text-gray-600">{g.sousGroupe}</p>}
                      <p className="mt-1 text-sm text-gray-500">
                        {g.enfants.length} enfant{g.enfants.length > 1 ? 's' : ''}
                        {c && c.aPointer < g.enfants.length && (
                          <>
                            {' · '}
                            <span className="text-green-700">{c.presents} ✓</span>
                            {' '}
                            <span className="text-red-700">{c.absents} ✗</span>
                            {c.aPointer > 0 && <span className="text-gray-500"> · {c.aPointer} à pointer</span>}
                          </>
                        )}
                      </p>
                    </div>
                    <ChevronRight className="h-6 w-6 flex-shrink-0 text-gray-400" />
                  </Vers>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </Cadre>
  );
  }

  return <Contexte.Provider value={contexte}>{ecran}</Contexte.Provider>;
}

/* ------------------------------------------------------------------------ */

function Cadre({ children }: { children: React.ReactNode }) {
  const { pleinePage } = useContext(Contexte);
  return (
    <div className={pleinePage ? 'min-h-screen bg-gray-50 text-gray-900' : 'overflow-hidden rounded-2xl bg-gray-50 text-gray-900 ring-1 ring-gray-200'}>
      {children}
    </div>
  );
}

function Erreur({ texte }: { texte: string }) {
  return (
    <div className="mb-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <span>{texte}</span>
    </div>
  );
}

function EnTete({
  liste,
  rafraichissement,
  onRecharger,
}: {
  liste: ListeDAppel;
  rafraichissement: boolean;
  onRecharger: () => void;
}) {
  return (
    <header className="border-b border-gray-200 bg-white">
      <div className="flex items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-bold text-gray-900">{liste.centre.nom}</h1>
          <p className="text-sm text-gray-600">
            {liste.semaine.libelle} · {semaineEnClair(liste.semaine.debut, liste.semaine.fin)}
          </p>
        </div>
        <button
          type="button"
          onClick={onRecharger}
          disabled={rafraichissement}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
          aria-label="Recharger la liste"
          title="Recharger la liste"
        >
          <RefreshCw className={`h-5 w-5 ${rafraichissement ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </header>
  );
}

/* ------------------------------------------------------------------------ */
/*  La feuille d'un groupe                                                   */
/* ------------------------------------------------------------------------ */

function FeuilleDuGroupe({
  groupe,
  montrerGroupe = false,
  jours,
  jour,
  jourCourant,
  erreur,
  enCours,
  essentiels,
  essentielsErreur,
  photos,
  reglements,
  onRelireEssentiels,
  onJour,
  onTaper,
  onTousLa,
}: {
  groupe: GroupeDAppel;
  /** Tout le centre : le groupe de chaque enfant sous son nom, et pas de « tout le monde est là ». */
  montrerGroupe?: boolean;
  jours: ListeDAppel['jours'];
  jour: JourCle | null;
  jourCourant: ListeDAppel['jours'][number] | null;
  erreur: string | null;
  enCours: Set<string>;
  essentiels: Map<string, EssentielsDeLEnfant> | null;
  essentielsErreur: string | null;
  photos: Map<string, string>;
  reglements: Map<string, ReglementAffiche> | null;
  onRelireEssentiels: () => void;
  onJour: (j: JourCle) => void;
  onTaper: (e: EnfantDuLien) => void;
  onTousLa: (enfants: EnfantDuLien[]) => void;
}) {
  const { hautCollant } = useContext(Contexte);
  const [onglet, setOnglet] = useState<'presences' | 'a_savoir'>('presences');
  const [recherche, setRecherche] = useState('');
  const visibles = useMemo(() => filtrerEnfants(groupe.enfants, recherche), [groupe, recherche]);
  const comptes = jour ? compterLeJour(groupe.enfants, jour) : null;
  const parts = useMemo(() => partagerLeGroupe(groupe.enfants, essentiels), [groupe, essentiels]);

  const ongletStyle = (actif: boolean) =>
    `flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
      actif ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
    }`;

  return (
    <>
      <div className={`sticky ${hautCollant} z-10 border-b border-gray-200 bg-white/95 backdrop-blur`}>
        <div className="flex items-center gap-1 px-2 pt-2">
          <Vers
            vue={VUE_GROUPES}
            className="flex h-11 w-11 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
            aria-label="Revenir aux groupes"
          >
            <ChevronLeft className="h-6 w-6" />
          </Vers>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-gray-900">{groupe.stage}</h2>
            {groupe.sousGroupe && <p className="truncate text-sm text-gray-600">{groupe.sousGroupe}</p>}
          </div>
          <span className="pr-2 text-sm text-gray-500">{groupe.enfants.length}</span>
        </div>

        {/*
          Présences | À savoir : pointer le groupe, et ce qu'il faut savoir sans
          ouvrir chaque fiche. Pas « Appel » : en Belgique, on entend un coup de
          téléphone (demandé le 25/10/2026).
        */}
        <div className="mx-4 mt-2 flex gap-1 rounded-xl bg-gray-100 p-1" role="tablist" aria-label="Vue">
          <button type="button" role="tab" aria-selected={onglet === 'presences'} onClick={() => setOnglet('presences')} className={ongletStyle(onglet === 'presences')}>
            Présences
          </button>
          <button type="button" role="tab" aria-selected={onglet === 'a_savoir'} onClick={() => setOnglet('a_savoir')} className={ongletStyle(onglet === 'a_savoir')}>
            À savoir
            {essentiels && parts.aSignaler.length > 0 && (
              <span className="ml-1.5 inline-flex min-w-[1.4rem] items-center justify-center rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-900">
                {parts.aSignaler.length}
              </span>
            )}
          </button>
        </div>

        {onglet === 'presences' && (
        <div className="mt-2 flex gap-1.5 overflow-x-auto px-4 pb-2" role="tablist" aria-label="Jour">
          {jours.map((j) => {
            const actif = j.cle === jour;
            const pointable = jourPointable(j);
            return (
              <button
                key={j.cle}
                type="button"
                role="tab"
                aria-selected={actif}
                disabled={!pointable}
                onClick={() => onJour(j.cle)}
                className={`flex min-w-[4.2rem] flex-1 flex-col items-center rounded-xl px-2 py-1.5 text-sm font-medium transition-colors ${
                  actif
                    ? 'bg-primary-600 text-white shadow-sm'
                    : pointable
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-300'
                }`}
                title={j.ferie ? `Férié : ${j.ferie}` : j.sansSession ? 'Pas de stage ce jour' : NOM_DU_JOUR[j.cle]}
              >
                <span>{NOM_COURT_DU_JOUR[j.cle]}</span>
                <span className={`text-xs ${actif ? 'text-primary-100' : 'text-gray-500'}`}>
                  {j.date.slice(8, 10)}/{j.date.slice(5, 7)}
                </span>
              </button>
            );
          })}
        </div>

        )}

        {onglet === 'presences' && groupe.enfants.length > 8 && (
          <div className="px-4 pb-2">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Chercher un prénom…"
                className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-4 text-base focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                aria-label="Chercher un enfant"
              />
            </label>
          </div>
        )}
        {onglet === 'a_savoir' && <div className="pb-2" />}
      </div>

      {onglet === 'a_savoir' ? (
        <VueASavoir
          parts={parts}
          montrerGroupe={montrerGroupe}
          charge={essentiels !== null}
          erreur={essentielsErreur}
          photos={photos}
          onRelire={onRelireEssentiels}
        />
      ) : (
      <main className="px-4 pb-24 pt-3">
        {erreur && <Erreur texte={erreur} />}

        {jourCourant && comptes && (
          <div className="mb-3 flex items-center justify-between gap-2 text-sm text-gray-600">
            <span className="font-medium text-gray-900">
              {NOM_DU_JOUR[jourCourant.cle]} {dateEnClair(jourCourant.date).slice(0, 5)}
            </span>
            <span className="text-right">
              <span className="font-semibold text-green-700">{comptes.presents}</span> ✓ ·{' '}
              <span className="font-semibold text-red-700">{comptes.absents}</span> ✗ ·{' '}
              <span className="font-semibold text-gray-700">{comptes.aPointer}</span> à pointer
            </span>
          </div>
        )}

        {!jour && (
          <p className="rounded-xl bg-gray-100 p-4 text-center text-gray-600">Aucun jour à pointer cette semaine.</p>
        )}

        {jour && comptes && comptes.aPointer > 0 && !recherche && !montrerGroupe && (
          <button
            type="button"
            onClick={() => onTousLa(groupe.enfants)}
            className="mb-3 w-full rounded-xl bg-green-50 px-4 py-3 text-sm font-medium text-green-800 ring-1 ring-green-200 active:bg-green-100"
          >
            {comptes.aPointer === groupe.enfants.length
              ? `Tout le monde est là (${comptes.aPointer})`
              : comptes.aPointer === 1
                ? 'L’enfant à pointer est là'
                : `Les ${comptes.aPointer} enfants à pointer sont là`}
          </button>
        )}

        {jour && visibles.length === 0 && (
          <p className="py-10 text-center text-gray-500">Personne ne répond à « {recherche} ».</p>
        )}

        {jour && visibles.length > 0 && (
          <ul className="divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
            {visibles.map((e) => (
              <LigneEnfant
                key={e.registrationId}
                enfant={e}
                etat={etatDuJour(e, jour)}
                occupe={enCours.has(e.registrationId)}
                photo={photos.get(e.registrationId) ?? null}
                signes={indicateurs(essentiels?.get(e.registrationId))}
                reglement={reglements?.get(e.registrationId) ?? null}
                sousTitre={montrerGroupe ? `${e.stage}${e.sousGroupe ? ` · ${e.sousGroupe}` : ''}` : null}
                onTaper={() => onTaper(e)}
              />
            ))}
          </ul>
        )}

        <p className="mt-4 text-center text-xs text-gray-400">
          Un tap sur la case : présent, encore un : absent, encore un : à pointer. Le nom ouvre la fiche.
        </p>
      </main>
      )}
    </>
  );
}

/* ------------------------------------------------------------------------ */
/*  À savoir : ce que le groupe doit savoir, sans ouvrir chaque fiche        */
/* ------------------------------------------------------------------------ */

function VueASavoir({
  parts,
  montrerGroupe = false,
  charge,
  erreur,
  photos,
  onRelire,
}: {
  parts: ReturnType<typeof partagerLeGroupe<EnfantDuLien>>;
  montrerGroupe?: boolean;
  charge: boolean;
  erreur: string | null;
  photos: Map<string, string>;
  onRelire: () => void;
}) {
  return (
    <main className="px-4 pb-24 pt-3">
      {erreur && (
        <div className="mb-3 flex items-start justify-between gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <span className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            {erreur}
          </span>
          <button type="button" onClick={onRelire} className="flex-shrink-0 font-medium underline">
            Réessayer
          </button>
        </div>
      )}

      {!charge && !erreur && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      )}

      {charge && parts.aSignaler.length === 0 && (
        <p className="rounded-xl bg-green-50 p-4 text-center text-sm text-green-800 ring-1 ring-green-200">
          Rien à signaler dans ce groupe : ni allergie, ni médicament, ni départ particulier, et photos permises pour tous.
        </p>
      )}

      {charge && parts.aSignaler.length > 0 && (
        <ul className="space-y-3">
          {parts.aSignaler.map(({ enfant, lignes }) => (
            <li key={enfant.registrationId} className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
              <Vers vue={{ nom: 'enfant', registrationId: enfant.registrationId }} className="block w-full p-3 active:bg-gray-50">
                <div className="flex items-center gap-3">
                  <Avatar photo={photos.get(enfant.registrationId) ?? null} prenom={enfant.prenom} nom={enfant.nom} taille="lg" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-base font-semibold text-gray-900">
                      {enfant.prenom} <span className="font-normal text-gray-700">{enfant.nom}</span>
                    </p>
                    {montrerGroupe && (
                      <p className="truncate text-xs text-gray-500">
                        {enfant.stage}
                        {enfant.sousGroupe ? ` · ${enfant.sousGroupe}` : ''}
                      </p>
                    )}
                    {enfant.presence?.notes && (
                      <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-amber-800">
                        <MessageSquare className="h-3 w-3 flex-shrink-0" />
                        {enfant.presence.notes}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-300" />
                </div>
                <dl className="mt-3 space-y-1.5">
                  {lignes.map((l) => (
                    <LigneASavoirVue key={l.cle} ligne={l} />
                  ))}
                </dl>
              </Vers>
            </li>
          ))}
        </ul>
      )}

      {charge && parts.sansRien.length > 0 && (
        <p className="mt-4 text-sm text-gray-500">
          <span className="font-medium text-gray-700">Rien à signaler</span> pour{' '}
          {parts.sansRien.map((e) => e.prenom).join(', ')} ({parts.sansRien.length}).
        </p>
      )}

      <p className="mt-4 text-center text-xs text-gray-400">
        Les « tout va bien » et « RAS » sont écartés, comme dans la fiche santé PDF. Le tétanos, le médecin et les
        téléphones sont dans la fiche de l’enfant.
      </p>
    </main>
  );
}

function LigneASavoirVue({ ligne }: { ligne: LigneASavoir }) {
  const icone =
    ligne.cle === 'photo' ? (
      <CameraOff className="h-4 w-4" />
    ) : ligne.cle === 'depart' ? (
      <ChevronRight className="h-4 w-4" />
    ) : (
      <HeartPulse className="h-4 w-4" />
    );
  return (
    <div className={`flex items-start gap-2 text-sm ${ligne.alerte ? 'text-amber-900' : 'text-gray-800'}`}>
      <span className={`mt-0.5 flex-shrink-0 ${ligne.alerte ? 'text-amber-600' : 'text-gray-400'}`}>{icone}</span>
      <dt className="w-24 flex-shrink-0 text-gray-500">{ligne.libelle}</dt>
      <dd className={`min-w-0 flex-1 ${ligne.alerte ? 'font-medium' : ''}`}>{ligne.texte}</dd>
    </div>
  );
}

/** La photo de l'enfant, ou ses initiales. */
function Avatar({ photo, prenom, nom, taille }: { photo: string | null; prenom: string; nom: string; taille: 'sm' | 'lg' }) {
  const dim = taille === 'lg' ? 'h-14 w-14 text-lg' : 'h-10 w-10 text-sm';
  if (photo) {
    return (
      <img
        src={photo}
        alt=""
        loading="lazy"
        className={`${dim} flex-shrink-0 rounded-full object-cover ring-1 ring-gray-200`}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={`${dim} flex flex-shrink-0 items-center justify-center rounded-full bg-primary-50 font-semibold text-primary-700 ring-1 ring-primary-100`}
    >
      {initiales(prenom, nom)}
    </span>
  );
}

function LigneEnfant({
  enfant,
  etat,
  occupe,
  photo,
  signes,
  reglement,
  sousTitre,
  onTaper,
}: {
  enfant: EnfantDuLien;
  etat: EtatDuJour;
  occupe: boolean;
  photo: string | null;
  signes: ReturnType<typeof indicateurs>;
  /** La coordination : le règlement colore la barre au bord gauche de la ligne. */
  reglement: ReglementAffiche | null;
  /** Tout le centre : le groupe de l'enfant, sous son nom. */
  sousTitre: string | null;
  onTaper: () => void;
}) {
  const fond = etat === 'present' ? 'bg-green-50/60' : etat === 'absent' ? 'bg-red-50/60' : 'bg-white';
  const barre = reglement ? couleurDeReglement(reglement.cle) : '';
  const caseStyle =
    etat === 'present'
      ? 'bg-green-600 text-white shadow-sm'
      : etat === 'absent'
        ? 'bg-red-600 text-white shadow-sm'
        : 'bg-gray-100 text-gray-300 ring-1 ring-inset ring-gray-200';
  const libelle = etat === 'present' ? 'Présent' : etat === 'absent' ? 'Absent' : 'À pointer';

  return (
    <li className={`relative ${fond} transition-colors`} title={reglement?.titre}>
      {barre && <span aria-hidden className={`absolute inset-y-0 left-0 w-1.5 ${barre}`} />}
      <div className="flex items-center gap-2 px-3 py-1.5">
        <Vers
          vue={{ nom: 'enfant', registrationId: enfant.registrationId }}
          className="flex min-w-0 flex-1 items-center gap-2.5 py-1.5"
          aria-label={`Fiche de ${enfant.prenom} ${enfant.nom}`}
        >
          <Avatar photo={photo} prenom={enfant.prenom} nom={enfant.nom} taille="sm" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-base font-semibold text-gray-900">{enfant.prenom}</span>
              <span className="truncate text-sm text-gray-600">{enfant.nom}</span>
              {signes.sante && (
                <HeartPulse className="h-4 w-4 flex-shrink-0 text-red-600" aria-label="Santé : à savoir" />
              )}
              {signes.pasDePhoto && (
                <CameraOff className="h-4 w-4 flex-shrink-0 text-amber-700" aria-label="Pas de photo" />
              )}
            </div>
            {sousTitre && <div className="truncate text-xs text-gray-500">{sousTitre}</div>}
            {enfant.presence?.notes && (
              <div className="mt-0.5 flex items-center gap-1 text-xs text-amber-800">
                <MessageSquare className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{enfant.presence.notes}</span>
              </div>
            )}
          </div>
        </Vers>

        <button
          type="button"
          onClick={onTaper}
          disabled={occupe}
          aria-label={`${libelle} — ${enfant.prenom} ${enfant.nom}`}
          className={`flex h-12 w-14 flex-shrink-0 items-center justify-center rounded-xl text-lg font-bold transition-colors disabled:opacity-60 ${caseStyle}`}
        >
          {occupe ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : etat === 'present' ? (
            <Check className="h-6 w-6" />
          ) : etat === 'absent' ? (
            <X className="h-6 w-6" />
          ) : (
            <span className="h-3 w-3 rounded-full bg-gray-300" aria-hidden />
          )}
        </button>
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------------ */
/*  La fiche de l'enfant                                                     */
/* ------------------------------------------------------------------------ */

function FicheDeLEnfant({
  source,
  enfant,
  photo,
  retour,
  onNoter,
  onCodeRequis,
}: {
  source: SourceDesPresences;
  enfant: EnfantDuLien | null;
  photo: string | null;
  retour: Vue;
  onNoter: (notes: string) => void;
  onCodeRequis: () => void;
}) {
  const { hautCollant } = useContext(Contexte);
  const [fiche, setFiche] = useState<FicheEnfant | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [brouillon, setBrouillon] = useState(enfant?.presence?.notes ?? '');
  const [noteGardee, setNoteGardee] = useState(false);
  const registrationId = enfant?.registrationId ?? null;

  useEffect(() => {
    if (!registrationId) {
      setChargement(false);
      return;
    }
    let annule = false;
    setChargement(true);
    setErreur(null);
    void (async () => {
      try {
        const lecture = await source.lireFiche(registrationId);
        if (annule) return;
        if (lecture.codeRequis) {
          onCodeRequis();
          return;
        }
        setFiche(lecture.fiche);
      } catch (e) {
        if (!annule) setErreur(messageDeRefus(e instanceof Error ? e.message : String(e)));
      } finally {
        if (!annule) setChargement(false);
      }
    })();
    return () => {
      annule = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, registrationId]);

  const Retour = (
    <div className={`sticky ${hautCollant} z-10 flex items-center gap-1 border-b border-gray-200 bg-white/95 px-2 py-2 backdrop-blur`}>
      <Vers
        vue={retour}
        className="flex h-11 w-11 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
        aria-label="Revenir à la liste"
      >
        <ChevronLeft className="h-6 w-6" />
      </Vers>
      {enfant && <Avatar photo={photo} prenom={enfant.prenom} nom={enfant.nom} taille="sm" />}
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-base font-semibold text-gray-900">
          {enfant ? `${enfant.prenom} ${enfant.nom}` : 'Fiche'}
        </h2>
        {enfant && (
          <p className="truncate text-sm text-gray-600">
            {enfant.stage}
            {enfant.sousGroupe ? ` · ${enfant.sousGroupe}` : ''}
          </p>
        )}
      </div>
    </div>
  );

  if (!enfant) {
    return (
      <>
        {Retour}
        <main className="px-4 py-10 text-center text-gray-600">
          <p>Cet enfant n’est plus dans la liste.</p>
          <Vers vue={retour} className="mt-4 inline-block font-medium text-primary-700">
            Revenir à la liste
          </Vers>
        </main>
      </>
    );
  }

  const alertes = fiche ? alertesDeLaFiche(fiche) : [];
  const age = fiche ? ageEnClair(fiche.dateNaissance, aujourdHui()) : null;

  return (
    <>
      {Retour}
      <main className="space-y-4 px-4 pb-24 pt-4">
        {erreur && <Erreur texte={erreur} />}
        {chargement && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        )}

        {fiche && (
          <>
            {alertes.length > 0 && (
              <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                  <AlertTriangle className="h-4 w-4" /> À savoir
                </h3>
                <ul className="mt-2 space-y-1 text-sm text-amber-900">
                  {alertes.map((a) => (
                    <li key={a}>• {a}</li>
                  ))}
                </ul>
              </section>
            )}

            <Section titre="Identité" icone={<Users className="h-4 w-4" />}>
              <Ligne libelle="Âge" valeur={age ? `${age}${fiche.dateNaissance ? ` (${dateEnClair(fiche.dateNaissance)})` : ''}` : null} />
              <Ligne libelle="École" valeur={fiche.ecole} />
            </Section>

            <Section titre="Parent" icone={<Phone className="h-4 w-4" />}>
              <Ligne libelle="Nom" valeur={`${fiche.parent.prenom} ${fiche.parent.nom}`.trim() || null} />
              <Telephone libelle="Téléphone" numero={fiche.parent.telephone} />
              <Telephone libelle="Autre téléphone" numero={fiche.parent.telephone2} />
            </Section>

            <Section titre="Départ" icone={<ChevronRight className="h-4 w-4" />}>
              <Ligne
                libelle="Repart seul·e"
                valeur={fiche.depart.partSeul === null ? null : fiche.depart.partSeul ? 'Oui' : 'Non'}
                alerte={fiche.depart.partSeul === true}
              />
              <Ligne libelle="Heure" valeur={fiche.depart.heure} />
              {fiche.depart.personnes.length > 0 && (
                <div className="pt-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Personnes autorisées</p>
                  <ul className="mt-1 space-y-1">
                    {fiche.depart.personnes.map((p, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-gray-900">
                          {p.prenom} {p.nom}
                          {p.lien ? <span className="text-gray-500"> · {p.lien}</span> : null}
                        </span>
                        {p.telephone && (
                          <a href={`tel:${p.telephone.replace(/\s/g, '')}`} className="font-medium text-primary-700">
                            {p.telephone}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>

            <Section titre="Santé" icone={<HeartPulse className="h-4 w-4" />}>
              <Ligne libelle="Allergies" valeur={ouiNon(fiche.sante.allergies, fiche.sante.allergiesDetails)} alerte={fiche.sante.allergies === true} />
              {fiche.sante.allergies && fiche.sante.allergiesConsequences && (
                <Ligne libelle="En cas de contact" valeur={fiche.sante.allergiesConsequences} alerte />
              )}
              <Ligne libelle="Régime" valeur={ouiNon(fiche.sante.regime, fiche.sante.regimeDetails)} alerte={fiche.sante.regime === true} />
              <Ligne libelle="Médicaments" valeur={ouiNon(fiche.sante.medicaments, fiche.sante.medicamentsDetails)} alerte={fiche.sante.medicaments === true} />
              {fiche.sante.medicaments && (
                <Ligne libelle="Les prend seul·e" valeur={fiche.sante.medicamentsAutonomie === null ? null : fiche.sante.medicamentsAutonomie ? 'Oui' : 'Non'} />
              )}
              <Ligne libelle="À savoir" valeur={fiche.sante.aSavoir} alerte={Boolean(fiche.sante.aSavoir)} />
              <Ligne libelle="Antécédents" valeur={fiche.sante.antecedents} />
              <Ligne libelle="Tétanos à jour" valeur={fiche.sante.tetanos === null ? null : fiche.sante.tetanos ? 'Oui' : 'Non'} />
              <Ligne libelle="Médecin" valeur={fiche.sante.medecinNom} />
              <Telephone libelle="Tél. médecin" numero={fiche.sante.medecinTelephone} />
            </Section>

            <Section titre="Activités et baignade" icone={<Waves className="h-4 w-4" />}>
              <Ligne
                libelle="Peut tout faire"
                valeur={fiche.activites.participe === null ? null : fiche.activites.participe ? 'Oui' : 'Non'}
                alerte={fiche.activites.participe === false}
              />
              <Ligne libelle="Restrictions" valeur={fiche.activites.restrictions} alerte={Boolean(fiche.activites.restrictions)} />
              <Ligne
                libelle="Sait nager"
                valeur={
                  fiche.activites.nage === null
                    ? null
                    : `${fiche.activites.nage ? 'Oui' : 'Non'}${fiche.activites.niveauNage ? ` (${fiche.activites.niveauNage})` : ''}`
                }
                alerte={fiche.activites.nage === false}
              />
              <Ligne libelle="Peur de l’eau" valeur={fiche.activites.peurEau === null ? null : fiche.activites.peurEau ? 'Oui' : 'Non'} alerte={fiche.activites.peurEau === true} />
              <Ligne libelle="Autres" valeur={fiche.activites.autres} />
            </Section>

            {fiche.inclusion && (
              <Section titre="Besoins particuliers" icone={<AlertTriangle className="h-4 w-4" />}>
                <Ligne libelle="Situation" valeur={fiche.inclusion.situation} />
                <Ligne libelle="Impact" valeur={fiche.inclusion.impact} />
                <Ligne libelle="Accompagnement" valeur={[fiche.inclusion.accompagnement, fiche.inclusion.accompagnementDetails].filter(Boolean).join(' — ') || null} />
                <Ligne libelle="Ce qui aide" valeur={fiche.inclusion.strategies} />
                <Ligne libelle="Aides techniques" valeur={fiche.inclusion.aides} />
                <Ligne libelle="Signes de stress" valeur={fiche.inclusion.signauxStress} />
                <Ligne libelle="Forces" valeur={fiche.inclusion.forces} />
                <Ligne libelle="Expérience" valeur={fiche.inclusion.experience} />
              </Section>
            )}

            <Section titre="Droit à l’image" icone={<Camera className="h-4 w-4" />}>
              {fiche.image ? (
                <>
                  <Ligne libelle="Espaces internes (groupe des familles)" valeur={fiche.image.espacesInternes ? 'Oui' : 'Non'} alerte={!fiche.image.espacesInternes} />
                  <Ligne libelle="Rapports d’activité" valeur={fiche.image.rapportsActivite ? 'Oui' : 'Non'} alerte={!fiche.image.rapportsActivite} />
                  <Ligne libelle="Site public" valeur={fiche.image.sitePublic ? 'Oui' : 'Non'} alerte={!fiche.image.sitePublic} />
                  <Ligne libelle="Réseaux sociaux" valeur={fiche.image.reseauxSociaux ? 'Oui' : 'Non'} alerte={!fiche.image.reseauxSociaux} />
                  <Ligne libelle="Imprimés" valeur={fiche.image.imprimes ? 'Oui' : 'Non'} alerte={!fiche.image.imprimes} />
                </>
              ) : (
                <p className="text-sm text-amber-800">
                  La famille n’a pas répondu : <strong>pas de photo</strong> tant que ce n’est pas réglé.
                </p>
              )}
            </Section>
          </>
        )}

        <Section titre="Note de la semaine" icone={<MessageSquare className="h-4 w-4" />}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onNoter(brouillon);
              setNoteGardee(true);
              setTimeout(() => setNoteGardee(false), 2000);
            }}
            className="flex items-center gap-2"
          >
            <input
              type="text"
              value={brouillon}
              onChange={(e) => setBrouillon(e.target.value)}
              placeholder="Part à 16h, maman appelle à midi…"
              className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              aria-label="Note"
            />
            <button type="submit" className="rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white">
              {noteGardee ? 'Gardée' : 'Garder'}
            </button>
          </form>
          <p className="mt-1 text-xs text-gray-500">Visible par toute l’équipe du centre et par la coordination.</p>
        </Section>
      </main>
    </>
  );
}

const ouiNon = (v: boolean | null, details: string | null): string | null => {
  if (v === null) return details;
  if (!v) return 'Non';
  return details ? `Oui — ${details}` : 'Oui';
};

function Section({ titre, icone, children }: { titre: string; icone: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <span className="text-gray-400">{icone}</span>
        {titre}
      </h3>
      <div className="mt-2 divide-y divide-gray-100">{children}</div>
    </section>
  );
}

function Ligne({ libelle, valeur, alerte = false }: { libelle: string; valeur: string | null; alerte?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
      <span className="flex-shrink-0 text-gray-500">{libelle}</span>
      <span className={`text-right ${valeur ? (alerte ? 'font-medium text-amber-900' : 'text-gray-900') : 'text-gray-300'}`}>
        {valeur ?? '—'}
      </span>
    </div>
  );
}

function Telephone({ libelle, numero }: { libelle: string; numero: string | null }) {
  if (!numero) return <Ligne libelle={libelle} valeur={null} />;
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span className="text-gray-500">{libelle}</span>
      <a
        href={`tel:${numero.replace(/\s/g, '')}`}
        className="inline-flex items-center gap-1.5 rounded-lg bg-primary-50 px-3 py-1.5 font-medium text-primary-800"
      >
        <Phone className="h-4 w-4" /> {numero}
      </a>
    </div>
  );
}
